import { useState } from 'react';
import { Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { planAnalysis, useAnalysisTypes, useInvalidateAnalyses, type AnalysisStaff } from '@/api/analyses';
import { errorMessage } from '@/api/errors';

export interface PlanAnalysisModalProps {
  projectId: string;
  opened: boolean;
  onClose: () => void;
  /** Delivered analyses of the project, offered as `supersedes`. */
  delivered: AnalysisStaff[];
}

export function PlanAnalysisModal({ projectId, opened, onClose, delivered }: PlanAnalysisModalProps) {
  const { t, i18n } = useTranslation('analyses');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateAnalyses();
  const { data: types } = useAnalysisTypes();
  const [typeKey, setTypeKey] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [supersedes, setSupersedes] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const plan = useMutation({ mutationFn: () => planAnalysis(projectId, { type_key: typeKey!, name, supersedes }), onSuccess: () => invalidate(undefined, projectId) });

  const submit = async () => {
    setError(null);
    try {
      const a = await plan.mutateAsync();
      onClose();
      await navigate({ to: '/staff/analyses/$analysisId', params: { analysisId: a.public_id } });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const lang = i18n.language as 'cs' | 'en';
  const labelOf = (n: unknown) => ((n as Record<string, string>)?.[lang] ?? (n as Record<string, string>)?.cs ?? '') as string;

  return (
    <Modal opened={opened} onClose={onClose} title={t('plan.title')}>
      <Stack gap="md">
        <Select
          label={t('plan.type')}
          data={(types ?? []).filter((x) => x.is_active).map((x) => ({ value: x.key, label: `${labelOf(x.name)} (${x.key})` }))}
          value={typeKey}
          onChange={setTypeKey}
          required
          searchable
        />
        <TextInput label={t('plan.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
        {delivered.length > 0 && (
          <Select
            label={t('plan.supersedes')}
            data={delivered.map((a) => ({ value: a.public_id, label: `${labelOf(a.name)} · ${a.public_id}` }))}
            value={supersedes}
            onChange={setSupersedes}
            clearable
          />
        )}
        {error && (
          <Text c="red" role="alert" fz={14}>
            {error}
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {tc('common.cancel')}
          </Button>
          <Button loading={plan.isPending} disabled={!typeKey} onClick={() => void submit()}>
            {t('plan.create')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
